import {
    Button,
    Card,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components';
import {
    BriefcaseRegular,
    BuildingRegular,
    CheckmarkCircleRegular,
    EditRegular,
    LocationRegular,
    PersonRegular,
    SettingsRegular,
} from '@fluentui/react-icons';
import { format, parseISO } from 'date-fns';
import { useAtom } from 'jotai';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientOnboardingForm, MpmProjectInput } from '../../api';
import { useClientOnboardingApi } from '../../api/hooks/ClientOnboardingApi';
import {
    useBusinessUnitOptionsGet,
    useLineOfBusinessOptionsGet,
    useOfficeOptionsGet,
    useStaffOptionsGet,
} from '../../api/hooks/OptionsApi';
import { ClientOnboardingFormAtom } from '../../store/ClientOnboardingFormAtom';
import { filterEnabledSystems, includesMpm } from '../../utils/features';
import { addressPrinter, einPrinter, ssnPrinter } from '../../utils/printerUtils';
import TopBar from '../common/TopBar';
import Container from '../layout/Container';

const useStyles = makeStyles({
    head: {
        textAlign: 'center',
        margin: '4px 0 22px',
    },
    headTitle: {
        fontSize: '22px',
        fontWeight: tokens.fontWeightBold,
        display: 'block',
    },
    headSub: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase300,
    },
    grid: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '820px',
        margin: '0 auto',
    },
    card: {
        padding: 0,
        overflow: 'hidden',
    },
    cardHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: '16px 20px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    cardIcon: {
        width: '30px',
        height: '30px',
        borderRadius: '8px',
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
    },
    cardTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightBold,
        flex: 1,
    },
    body: {
        padding: '16px 20px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '14px 24px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        minWidth: 0,
    },
    fieldFull: {
        gridColumn: '1 / -1',
    },
    label: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontWeight: tokens.fontWeightSemibold,
    },
    value: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightMedium,
    },
    valueEmpty: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
        fontWeight: tokens.fontWeightRegular,
    },
    project: {
        gridColumn: '1 / -1',
        padding: '12px 16px',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: '10px',
    },
    projectName: {
        fontWeight: tokens.fontWeightSemibold,
    },
    projectMeta: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        marginTop: '3px',
    },
    footer: {
        display: 'flex',
        gap: '10px',
        maxWidth: '820px',
        margin: '20px auto 0',
        alignItems: 'center',
    },
    spacer: {
        flex: 1,
    },
});

const formatIsoDate = (iso: string | undefined): string => {
    if (!iso) return '';
    try {
        return format(parseISO(iso), 'MM/dd/yyyy');
    } catch {
        return iso;
    }
};

const ReviewField = ({ label, value, full }: { label: string; value?: string; full?: boolean }) => {
    const styles = useStyles();
    const empty = !value || value.trim() === '';
    return (
        <div className={full ? `${styles.field} ${styles.fieldFull}` : styles.field}>
            <span className={styles.label}>{label}</span>
            <span className={empty ? `${styles.value} ${styles.valueEmpty}` : styles.value}>
                {empty ? 'Not provided' : value}
            </span>
        </div>
    );
};

const ReviewPage = () => {
    const styles = useStyles();
    const [clientOnboardingForm] = useAtom(ClientOnboardingFormAtom);
    const { clientType, generalSection, contactSection, adminSection, mpmSection } = clientOnboardingForm;
    // ModernPM is stripped here when disabled (VITE_ENABLE_MPM) so it is neither shown nor submitted.
    const systems = filterEnabledSystems(clientOnboardingForm.systems);
    const showMpm = includesMpm(systems);
    const { data: businessUnitOptions } = useBusinessUnitOptionsGet();
    const { data: lineOfBusinessOptions } = useLineOfBusinessOptionsGet();
    const { data: staffOptions } = useStaffOptionsGet();
    const { data: officeOptions } = useOfficeOptionsGet();

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { ClientsApi } = useClientOnboardingApi();
    const isIndividualClient = clientType === 'Individual' || clientType === 'Staff';
    const isStaff = clientType === 'Staff';

    // Submit form — mapping preserved exactly from the prior implementation.
    const submitToBackend = () => {
        setSubmitting(true);
        setSubmitError(null);

        const mpmProjects: MpmProjectInput[] | undefined = showMpm
            ? (mpmSection?.projects ?? []).map((p) => ({
                  templateId: Number(p.templateId),
                  name: p.projectName || undefined,
                  billingDescription: p.billingDescription || undefined,
                  projectOwnerId: p.projectOwnerId,
                  coverageStartDate: p.coverageStart ? format(parseISO(p.coverageStart), 'yyyy-MM-dd') : undefined,
                  coverageEndDate: p.coverageEnd ? format(parseISO(p.coverageEnd), 'yyyy-MM-dd') : undefined,
                  startDate: p.projectStart ? format(parseISO(p.projectStart), 'yyyy-MM-dd') : undefined,
              }))
            : undefined;

        const { mpmSection: _mpmSection, ...formWithoutMpmSection } = clientOnboardingForm;
        const trimmedForm: ClientOnboardingForm = {
            ...formWithoutMpmSection,
            systems,
            mpmProjects,
            adminSection: {
                ...clientOnboardingForm.adminSection,
                yearEnd: adminSection.yearEnd.replace('/', ''),
                primaryPartnerId: isStaff ? undefined : adminSection.primaryPartnerId,
                rainmakerId: isStaff ? undefined : adminSection.rainmakerId,
                billerId: isStaff ? undefined : adminSection.billerId,
                lineOfBusinessId: isStaff ? undefined : adminSection.lineOfBusinessId,
            },
            generalSection: {
                ...clientOnboardingForm.generalSection,
                businessSection: {
                    ...clientOnboardingForm.generalSection.businessSection,
                    dateIncorporated: generalSection.businessSection?.dateIncorporated
                        ? format(generalSection.businessSection.dateIncorporated, 'yyyy-MM-dd')
                        : null,
                },
            },
        };

        ClientsApi.clientsPost({ clientOnboardingForm: trimmedForm })
            .then((response) => {
                navigate('/ClientSuccess', { replace: true, state: { client: response.data } });
            })
            .catch((error) => {
                // Show the backend's reason (e.g. CCH or ModernPM validation) when it sent one: the API returns
                // ProblemDetails with the handler's message in `title` (`detail` is empty today).
                const problem = error?.response?.data;
                const detail = problem?.detail || problem?.title;
                setSubmitError(
                    typeof detail === 'string' && detail.trim()
                        ? `There was an error creating the client: ${detail}`
                        : 'There was an error creating the client. Please try again.'
                );
                console.error('Error', error);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const editSection = (key: string) => navigate('/FormPage', { state: { editSection: key } });

    const ReviewCard = ({
        icon,
        title,
        sectionKey,
        children,
    }: {
        icon: ReactNode;
        title: string;
        sectionKey: string;
        children: ReactNode;
    }) => (
        <Card className={styles.card}>
            <div className={styles.cardHead}>
                <span className={styles.cardIcon} aria-hidden>
                    {icon}
                </span>
                <Text className={styles.cardTitle}>{title}</Text>
                <Button appearance="transparent" icon={<EditRegular />} onClick={() => editSection(sectionKey)}>
                    Edit
                </Button>
            </div>
            <div className={styles.body}>{children}</div>
        </Card>
    );

    const personName = [generalSection.individualSection?.firstName, generalSection.individualSection?.lastName]
        .filter(Boolean)
        .join(' ');

    return (
        <Container>
            <TopBar />
            <div className={styles.head}>
                <Text className={styles.headTitle}>Review &amp; Submit</Text>
                <Text className={styles.headSub}>
                    Confirm the details below. The client will be created in {(systems ?? []).join(' and ') || '—'}.
                </Text>
            </div>

            <div className={styles.grid}>
                {/* Profile */}
                <ReviewCard
                    icon={isIndividualClient ? <PersonRegular /> : <BuildingRegular />}
                    title="Profile"
                    sectionKey="profile"
                >
                    <ReviewField label="Systems" value={(systems ?? []).join(', ')} full />
                    {isIndividualClient ? (
                        <>
                            <ReviewField label="Name" value={personName} />
                            <ReviewField
                                label="SSN"
                                value={ssnPrinter(generalSection.individualSection?.ssn) ?? undefined}
                            />
                        </>
                    ) : (
                        <ReviewField label="Business Name" value={generalSection.businessSection?.businessName} />
                    )}
                    <ReviewField label="Client Type" value={clientType} />
                    <ReviewField
                        label="Office"
                        value={officeOptions?.find((o) => o.id === generalSection.officeId)?.name}
                    />
                    <ReviewField
                        label="Business Unit"
                        value={businessUnitOptions?.find((b) => b.id === generalSection.businessUnitId)?.name}
                    />
                </ReviewCard>

                {/* Contact */}
                <ReviewCard icon={<LocationRegular />} title="Contact" sectionKey="contact">
                    <ReviewField
                        label={isIndividualClient ? 'Home Address' : 'Business Address'}
                        value={addressPrinter(contactSection.mainAddress)}
                        full
                    />
                    <ReviewField label="Mailing Address" value={addressPrinter(contactSection.mailingAddress)} full />
                    <ReviewField
                        label={isIndividualClient ? 'Main Phone' : 'Business Phone'}
                        value={contactSection.mainPhone}
                    />
                    <ReviewField label="Mobile Phone" value={contactSection.mobilePhone ?? undefined} />
                    <ReviewField
                        label={isIndividualClient ? 'Personal Email' : 'Business Email'}
                        value={contactSection.mainEmail}
                    />
                </ReviewCard>

                {/* Admin */}
                <ReviewCard icon={<SettingsRegular />} title="Admin" sectionKey="admin">
                    <ReviewField label="Year End" value={adminSection.yearEnd} />
                    <ReviewField
                        label="Manager"
                        value={staffOptions?.find(({ id }) => id === adminSection.managerId)?.name}
                    />
                    {!isStaff && (
                        <>
                            <ReviewField
                                label="Line of Business"
                                value={lineOfBusinessOptions?.find((p) => p.id === adminSection.lineOfBusinessId)?.name}
                            />
                            <ReviewField
                                label="Primary Partner"
                                value={staffOptions?.find(({ id }) => id === adminSection.primaryPartnerId)?.name}
                            />
                            <ReviewField
                                label="Biller"
                                value={staffOptions?.find(({ id }) => id === adminSection.billerId)?.name}
                            />
                            <ReviewField
                                label="Rainmaker"
                                value={staffOptions?.find(({ id }) => id === adminSection.rainmakerId)?.name}
                            />
                        </>
                    )}
                    {!isIndividualClient && (
                        <>
                            <ReviewField
                                label="EIN"
                                value={einPrinter(generalSection.businessSection?.ein) ?? undefined}
                            />
                            {generalSection.businessSection?.dateIncorporated && (
                                <ReviewField
                                    label="Incorporated Date"
                                    value={format(generalSection.businessSection.dateIncorporated, 'yyyy-MM-dd')}
                                />
                            )}
                            <ReviewField label="Business Code" value={generalSection.businessSection?.businessCode} />
                        </>
                    )}
                </ReviewCard>

                {/* Projects */}
                {showMpm && (
                    <ReviewCard icon={<BriefcaseRegular />} title="ModernPM Projects" sectionKey="projects">
                        {mpmSection?.projects?.length ? (
                            mpmSection.projects.map((project, idx) => (
                                <div key={idx} className={styles.project}>
                                    <div className={styles.projectName}>{project.projectName}</div>
                                    <div className={styles.projectMeta}>
                                        Template {project.templateName} · Owner {project.projectOwnerName} · Coverage{' '}
                                        {formatIsoDate(project.coverageStart)} – {formatIsoDate(project.coverageEnd)} ·
                                        Start {formatIsoDate(project.projectStart)}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <ReviewField label="" value="" full />
                        )}
                    </ReviewCard>
                )}
            </div>

            <div className={styles.footer}>
                <Button appearance="secondary" onClick={() => navigate('/FormPage')} disabledFocusable={submitting}>
                    Back to Edit
                </Button>
                <span className={styles.spacer} />
                {submitError && (
                    <MessageBar key="submitError" intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>Error Creating Client</MessageBarTitle>
                            {submitError}
                        </MessageBarBody>
                    </MessageBar>
                )}
                <Button
                    type="button"
                    appearance="primary"
                    size="large"
                    icon={submitting ? <Spinner size="tiny" /> : <CheckmarkCircleRegular />}
                    disabledFocusable={submitting}
                    onClick={submitToBackend}
                >
                    {submitting ? 'Creating…' : 'Create Client'}
                </Button>
            </div>
        </Container>
    );
};

export default ReviewPage;
